import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  Timeline as TimelineIcon,
  Star as StarIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  TrendingFlat as TrendingFlatIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
  marginBottom: theme.spacing(3),
}));

const MetricCard = styled(Card)(({ theme, gradient }) => ({
  background: gradient,
  color: 'white',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '50%',
    height: '100%',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '0 0 0 100%',
  }
}));

const ProgressCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  position: 'relative',
  overflow: 'hidden'
}));

const Analytics = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [chartType, setChartType] = useState('line');

  // Mock Analytics Data
  const analyticsData = {
    overview: {
      totalRevenue: 45670,
      totalOrders: 234,
      totalCustomers: 187,
      totalProducts: 45,
      growthRate: 12.5,
      conversionRate: 3.2,
      avgOrderValue: 195
    },
    salesData: [
      { date: '২০২৪-০১-১০', revenue: 3200, orders: 12, customers: 8 },
      { date: '২০২৪-০১-১১', revenue: 4100, orders: 15, customers: 11 },
      { date: '২০২৪-০১-১২', revenue: 2800, orders: 10, customers: 7 },
      { date: '২০২৪-০১-১৩', revenue: 5200, orders: 18, customers: 14 },
      { date: '২০২৪-০১-১৪', revenue: 3900, orders: 14, customers: 10 },
      { date: '২০২৪-০১-১৫', revenue: 4800, orders: 17, customers: 12 },
      { date: '২০২৪-০১-১৬', revenue: 5100, orders: 19, customers: 15 }
    ],
    topProducts: [
      { name: 'টাজা টমেটো', sold: 245, revenue: 29400, profit: 8820, rating: 4.8 },
      { name: 'দেশি পেঁয়াজ', sold: 189, revenue: 15120, profit: 4536, rating: 4.6 },
      { name: 'বাসমতি চাল', sold: 156, revenue: 28080, profit: 8424, rating: 4.9 },
      { name: 'মুরগির মাংস', sold: 98, revenue: 34300, profit: 10290, rating: 4.7 },
      { name: 'গরুর দুধ', sold: 234, revenue: 15210, profit: 4563, rating: 4.5 }
    ],
    customerInsights: [
      { segment: 'নিয়মিত ক্রেতা', count: 45, percentage: 35, avgSpend: 320 },
      { segment: 'নতুন ক্রেতা', count: 62, percentage: 48, avgSpend: 150 },
      { segment: 'প্রিমিয়াম ক্রেতা', count: 18, percentage: 14, avgSpend: 850 },
      { segment: 'সিজনাল ক্রেতা', count: 4, percentage: 3, avgSpend: 200 }
    ],
    recentActivity: [
      { type: 'order', message: 'রহিম উদ্দিন ৳৮৫০ এর অর্ডার দিয়েছেন', time: '১০ মিনিট আগে', icon: <ShoppingCartIcon /> },
      { type: 'customer', message: 'নতুন গ্রাহক ফাতেমা খাতুন রেজিস্ট্রেশন করেছেন', time: '২৫ মিনিট আগে', icon: <PeopleIcon /> },
      { type: 'review', message: 'করিম মিয়া টমেটোকে ৫ স্টার দিয়েছেন', time: '৪৫ মিনিট আগে', icon: <StarIcon /> },
      { type: 'stock', message: 'গাজরের স্টক কমে গেছে (৫ কেজি বাকি)', time: '১ ঘন্টা আগে', icon: <InventoryIcon /> },
      { type: 'payment', message: 'ORD-002 এর পেমেন্ট সফল হয়েছে', time: '২ ঘন্টা আগে', icon: <PaymentIcon /> }
    ]
  };

  const dateRangeOptions = [
    { value: '7days', label: 'গত ৭ দিন' },
    { value: '30days', label: 'গত ৩০ দিন' },
    { value: '3months', label: 'গত ৩ মাস' },
    { value: '6months', label: 'গত ৬ মাস' },
    { value: '1year', label: 'গত ১ বছর' }
  ];

  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
    if (value < 0) return <TrendingDownIcon sx={{ color: '#f44336' }} />;
    return <TrendingFlatIcon sx={{ color: '#ff9800' }} />;
  };

  const getTrendColor = (value) => {
    if (value > 0) return '#4caf50';
    if (value < 0) return '#f44336';
    return '#ff9800';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
              <AssessmentIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                বিক্রয় বিশ্লেষণ
              </Typography>
              <Typography variant="body1" color="text.secondary">
                আপনার দোকানের সম্পূর্ণ বিক্রয় ও কর্মক্ষমতা বিশ্লেষণ
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>সময়কাল</InputLabel>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                label="সময়কাল"
                MenuProps={{ disableScrollLock: true }}
              >
                {dateRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <ButtonGroup variant="outlined" size="small">
              <Tooltip title="রিফ্রেশ">
                <IconButton><RefreshIcon /></IconButton>
              </Tooltip>
              <Tooltip title="ফিল্টার">
                <IconButton><FilterIcon /></IconButton>
              </Tooltip>
              <Tooltip title="এক্সপোর্ট">
                <IconButton><DownloadIcon /></IconButton>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Box>
      </StyledPaper>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ৳{analyticsData.overview.totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মোট বিক্রয়
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(12.5)}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      +১২.৫% বৃদ্ধি
                    </Typography>
                  </Box>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {analyticsData.overview.totalOrders}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মোট অর্ডার
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(8.3)}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      +৮.৩% বৃদ্ধি
                    </Typography>
                  </Box>
                </Box>
                <ShoppingCartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {analyticsData.overview.totalCustomers}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    মোট গ্রাহক
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(15.7)}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      +১৫.৭% বৃদ্ধি
                    </Typography>
                  </Box>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ৳{analyticsData.overview.avgOrderValue}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    গড় অর্ডার মূল্য
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(5.2)}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      +৫.২% বৃদ্ধি
                    </Typography>
                  </Box>
                </Box>
                <AssessmentIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Charts and Analytics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Sales Chart */}
        <Grid item xs={12} lg={8}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                বিক্রয় ট্রেন্ড
              </Typography>
              <ButtonGroup size="small">
                <Button 
                  variant={chartType === 'line' ? 'contained' : 'outlined'}
                  onClick={() => setChartType('line')}
                  startIcon={<ShowChartIcon />}
                >
                  লাইন
                </Button>
                <Button 
                  variant={chartType === 'bar' ? 'contained' : 'outlined'}
                  onClick={() => setChartType('bar')}
                  startIcon={<BarChartIcon />}
                >
                  বার
                </Button>
                <Button 
                  variant={chartType === 'area' ? 'contained' : 'outlined'}
                  onClick={() => setChartType('area')}
                  startIcon={<TimelineIcon />}
                >
                  এরিয়া
                </Button>
              </ButtonGroup>
            </Box>
            
            {/* Simple Chart Representation */}
            <Box sx={{ height: 300, display: 'flex', alignItems: 'end', gap: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              {analyticsData.salesData.map((data, index) => (
                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <Tooltip title={`${data.date}: ৳${data.revenue.toLocaleString()}`}>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 40,
                        height: `${(data.revenue / 6000) * 250}px`,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 1,
                        mb: 1,
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
                        }
                      }}
                    />
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    {data.date.split('-')[2]}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#667eea', borderRadius: '50%' }} />
                <Typography variant="caption">বিক্রয়</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#f093fb', borderRadius: '50%' }} />
                <Typography variant="caption">অর্ডার</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#4facfe', borderRadius: '50%' }} />
                <Typography variant="caption">গ্রাহক</Typography>
              </Box>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} lg={4}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              কর্মক্ষমতা সূচক
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">রূপান্তর হার</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {analyticsData.overview.conversionRate}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={analyticsData.overview.conversionRate * 10} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">গ্রাহক সন্তুষ্টি</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>৯২%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={92} 
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">রিপিট কাস্টমার</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>৬৮%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={68} 
                color="info"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">স্টক টার্নওভার</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>৭৫%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                color="warning"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                A+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                সামগ্রিক স্কোর
              </Typography>
              <Typography variant="caption" color="success.main">
                চমৎকার কর্মক্ষমতা
              </Typography>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>

      {/* Detailed Analytics */}
      <Grid container spacing={3}>
        {/* Top Products */}
        <Grid item xs={12} lg={6}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              সেরা পণ্যসমূহ
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>পণ্য</TableCell>
                    <TableCell align="center">বিক্রিত</TableCell>
                    <TableCell align="center">রেটিং</TableCell>
                    <TableCell align="right">আয়</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.topProducts.map((product, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {product.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={product.sold} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 16, color: '#ffc107' }} />
                          <Typography variant="caption">{product.rating}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          ৳{product.revenue.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledPaper>
        </Grid>

        {/* Customer Insights */}
        <Grid item xs={12} lg={6}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              গ্রাহক বিশ্লেষণ
            </Typography>
            
            {analyticsData.customerInsights.map((segment, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {segment.segment}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {segment.count} জন
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ৳{segment.avgSpend}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={segment.percentage} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(135deg, hsl(${index * 60}, 70%, 60%) 0%, hsl(${index * 60 + 30}, 70%, 70%) 100%)`
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {segment.percentage}% মোট গ্রাহকের
                </Typography>
              </Box>
            ))}
            
            <Divider sx={{ my: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    ৳{analyticsData.customerInsights.reduce((sum, s) => sum + (s.count * s.avgSpend), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    মোট গ্রাহক ভ্যালু
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {analyticsData.customerInsights.reduce((sum, s) => sum + s.count, 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    সক্রিয় গ্রাহক
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                সাম্প্রতিক কার্যক্রম
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<VisibilityIcon />}
              >
                সব দেখুন
              </Button>
            </Box>
            
            <List>
              {analyticsData.recentActivity.map((activity, index) => (
                <ListItem key={index} divider={index < analyticsData.recentActivity.length - 1}>
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      bgcolor: activity.type === 'order' ? '#e3f2fd' : 
                                activity.type === 'customer' ? '#e8f5e9' :
                                activity.type === 'review' ? '#fff3e0' :
                                activity.type === 'stock' ? '#ffebee' : '#f3e5f5',
                      color: activity.type === 'order' ? '#1976d2' : 
                             activity.type === 'customer' ? '#388e3c' :
                             activity.type === 'review' ? '#f57c00' :
                             activity.type === 'stock' ? '#d32f2f' : '#7b1fa2'
                    }}>
                      {activity.icon}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={activity.message}
                    secondary={activity.time}
                    primaryTypographyProps={{ fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                  {activity.type === 'order' && (
                    <Chip label="নতুন" color="primary" size="small" />
                  )}
                  {activity.type === 'stock' && (
                    <Chip label="জরুরি" color="error" size="small" />
                  )}
                </ListItem>
              ))}
            </List>
          </StyledPaper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Analytics;
